import { useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { Button } from '@/components/ui/button'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { SectionHeader } from '@/components/custom/companies/section-header'
import {
  FINANCIALS_ACTUAL_VS_BUDGET_METRICS,
  getActualVsBudgetStateMessage,
} from '@/features/company-overview/actual-vs-budget'
import type {
  ActualVsBudgetComparison,
  CompanyActualVsBudget,
} from '@/features/actual-vs-budget-production/types'
import { useCompanyActualVsBudget } from '@/hooks/use-companies'
import { formatCurrency, formatDate, formatPercentage } from '@/lib/format'

const chartConfig = {
  actual: {
    label: 'Actual',
    color: 'var(--primary)',
  },
  budget: {
    label: 'Budget',
    color: 'var(--secondary)',
  },
} satisfies ChartConfig

const gridStroke = 'color-mix(in srgb, var(--primary) 10%, transparent)'

type TrendDatum = {
  period: string
  actual: number | null
  budget: number | null
  stateMessage: string | null
}

type TrendTooltipPayload = {
  dataKey?: string | number
  value?: number | string | null
  payload?: TrendDatum
}

type TrendMode = 'MONTHLY' | 'CUMULATIVE_FYTD'

const trendModeOptions: { value: TrendMode; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'CUMULATIVE_FYTD', label: 'Cumulative FYTD' },
]

function getTrendModeLabel(mode: TrendMode): string {
  return mode === 'MONTHLY' ? 'Monthly' : 'Cumulative FYTD'
}

function formatMonth(periodEnd: string | null): string {
  if (!periodEnd) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(periodEnd))
}

function toTrendData(points: ActualVsBudgetComparison[]): TrendDatum[] {
  return points.map((point) => ({
    period: formatMonth(point.periodEnd),
    actual: point.actual,
    budget: point.budget,
    stateMessage: getActualVsBudgetStateMessage(point),
  }))
}

function formatBudgetVariance(value: number | null): string | null {
  if (value === null) return null
  const percentage = formatPercentage(value)
  return `${value > 0 ? '+' : ''}${percentage} versus budget`
}

function formatDollarVariance(value: number | null): string {
  const formatted = formatCurrency(value)
  return value !== null && value > 0 ? `+${formatted}` : formatted
}

function TrendTooltip({
  active,
  payload,
  label,
  trendMode,
}: {
  active?: boolean
  payload?: TrendTooltipPayload[]
  label?: string | number
  trendMode: TrendMode
}) {
  if (!active || !payload?.length) return null

  const actual =
    payload.find((item) => item.dataKey === 'actual')?.value ?? null
  const budget =
    payload.find((item) => item.dataKey === 'budget')?.value ?? null
  const stateMessage = payload[0]?.payload?.stateMessage

  return (
    <ChartTooltipContent className="pointer-events-none border-border/40 px-2 py-1.5 shadow-lg">
      <div className="space-y-1">
        <Typography variant="body-sm" className="text-primary/60">
          {trendMode === 'CUMULATIVE_FYTD' ? `Through ${label}` : label}
        </Typography>
        <div className="flex items-center justify-between gap-4">
          <Typography as="span" variant="body-sm" className="text-primary/70">
            Actual
          </Typography>
          <Typography
            as="span"
            variant="body-sm"
            className="font-medium tabular-nums text-primary"
          >
            {formatCurrency(actual)}
          </Typography>
        </div>
        <div className="flex items-center justify-between gap-4">
          <Typography as="span" variant="body-sm" className="text-primary/70">
            Budget
          </Typography>
          <Typography
            as="span"
            variant="body-sm"
            className="font-medium tabular-nums text-primary"
          >
            {formatCurrency(budget)}
          </Typography>
        </div>
        {stateMessage ? (
          <Typography variant="body-sm" className="max-w-48 text-amber-900">
            {stateMessage}
          </Typography>
        ) : null}
      </div>
    </ChartTooltipContent>
  )
}

function MetricValue({
  label,
  value,
  format,
}: {
  label: string
  value: number | null
  format: (value: number | null) => string
}) {
  return (
    <div className="min-w-0">
      <Typography
        variant="body-sm"
        className="text-xs uppercase tracking-wide text-primary/50"
      >
        {label}
      </Typography>
      <Typography
        variant="metric-value"
        className="mt-1 truncate text-primary"
        title={format(value)}
      >
        {format(value)}
      </Typography>
    </div>
  )
}

function MetricComparisonGroup({
  comparison,
  trend,
  trendMode,
}: {
  comparison: ActualVsBudgetComparison
  trend: ActualVsBudgetComparison[]
  trendMode: TrendMode
}) {
  const chartData = toTrendData(trend)
  const trendModeLabel = getTrendModeLabel(trendMode)
  const trendStateMessages = [
    ...new Set(
      chartData.flatMap((point) =>
        point.stateMessage ? [point.stateMessage] : []
      )
    ),
  ]
  const stateMessage = getActualVsBudgetStateMessage(comparison)
  const budgetVariance = formatBudgetVariance(comparison.percentageVariance)

  return (
    <article
      aria-label={`${comparison.requestedMetric} Actual vs Budget`}
      data-state={comparison.state}
      className="min-w-0 py-5 first:pt-0 last:pb-0 @[48rem]:px-6 @[48rem]:py-0 @[48rem]:first:pl-0 @[48rem]:last:pr-0"
    >
      <Typography variant="h4">
        {comparison.metricLabel ?? comparison.requestedMetric}
      </Typography>

      <div
        className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1"
        aria-label={budgetVariance ?? undefined}
      >
        {comparison.percentageVariance !== null ? (
          <Typography
            as="span"
            variant="h1"
            className="text-4xl tabular-nums text-primary sm:text-5xl"
          >
            {comparison.percentageVariance > 0 ? '+' : ''}
            {formatPercentage(comparison.percentageVariance)}
          </Typography>
        ) : null}
        {budgetVariance ? (
          <Typography as="span" variant="h5" className="text-primary">
            versus budget
          </Typography>
        ) : null}
      </div>

      <div className="mt-5 grid grid-cols-3 gap-4">
        <MetricValue
          label="Actual"
          value={comparison.actual}
          format={formatCurrency}
        />
        <MetricValue
          label="Budget"
          value={comparison.budget}
          format={formatCurrency}
        />
        <MetricValue
          label="Dollar variance"
          value={comparison.dollarVariance}
          format={formatDollarVariance}
        />
      </div>

      {stateMessage ? (
        <div
          role="status"
          className="mt-4 rounded-sm border border-amber-700/20 bg-amber-50 px-3 py-2"
        >
          <Typography variant="body-sm" className="text-amber-900">
            {stateMessage}
          </Typography>
        </div>
      ) : null}

      {chartData.length > 0 ? (
        <>
          {trendStateMessages.map((message) => (
            <Typography
              key={message}
              variant="body-sm"
              role="status"
              className="sr-only"
            >
              {message}
            </Typography>
          ))}
          <Typography variant="body-sm" className="mt-5 text-primary/55">
            {trendModeLabel} performance · Millions (USD)
          </Typography>
          <ChartContainer
            config={chartConfig}
            className="mt-1 h-48 w-full aspect-auto [&_.recharts-tooltip-cursor]:stroke-primary/10"
            role="img"
            aria-label={`${trendModeLabel} ${comparison.requestedMetric} Actual and Budget trend`}
          >
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid vertical={false} stroke={gridStroke} />
              <XAxis
                dataKey="period"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={16}
                tick={{
                  fill: 'color-mix(in srgb, var(--primary) 65%, transparent)',
                  fontSize: 10,
                }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={(value: number) => formatCurrency(value)}
                tick={{
                  fill: 'color-mix(in srgb, var(--primary) 65%, transparent)',
                  fontSize: 10,
                }}
              />
              <ChartTooltip content={<TrendTooltip trendMode={trendMode} />} />
              <Line
                dataKey="actual"
                name="Actual"
                type="monotone"
                stroke="var(--color-actual)"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="budget"
                name="Budget"
                type="monotone"
                stroke="var(--color-budget)"
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ChartContainer>
        </>
      ) : (
        <div className="mt-5 flex h-48 items-center justify-center px-4 text-center">
          <Typography
            variant="body-sm"
            role="status"
            className="text-primary/55"
          >
            No {trendModeLabel.toLowerCase()} trend is available for{' '}
            {comparison.requestedMetric}.
          </Typography>
        </div>
      )}
    </article>
  )
}

export interface CompanyActualVsBudgetViewProps {
  data?: Omit<CompanyActualVsBudget, 'funds'> & {
    funds?: CompanyActualVsBudget['funds']
  }
  isLoading?: boolean
  isError?: boolean
  onRetry?: () => void
}

export function CompanyActualVsBudgetView({
  data,
  isLoading = false,
  isError = false,
  onRetry,
}: CompanyActualVsBudgetViewProps) {
  const [trendMode, setTrendMode] = useState<TrendMode>('MONTHLY')

  if (isLoading) {
    return (
      <section id="actual-vs-budget" aria-label="Actual vs Budget">
        <Skeleton className="h-96 w-full rounded-sm" />
        <Typography variant="body-sm" className="sr-only">
          Loading expanded Actual vs Budget
        </Typography>
      </section>
    )
  }

  if (isError) {
    return (
      <section
        id="actual-vs-budget"
        aria-label="Actual vs Budget"
        className="scroll-mt-4 space-y-3"
      >
        <SectionHeader title="Actual vs Budget" />
        <div className="flex min-h-48 flex-col items-center justify-center gap-3 p-4 text-center">
          <AlertCircle
            className="h-5 w-5 text-destructive"
            aria-hidden="true"
          />
          <Typography variant="body-sm" role="alert">
            Actual vs Budget could not be loaded.
          </Typography>
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
        </div>
      </section>
    )
  }

  if (!data || data.comparisons.length === 0) {
    return (
      <section
        id="actual-vs-budget"
        aria-label="Actual vs Budget"
        className="scroll-mt-4 space-y-3"
      >
        <SectionHeader title="Actual vs Budget" />
        <div className="flex min-h-48 items-center justify-center p-4 text-center">
          <Typography
            variant="body-sm"
            role="status"
            className="text-primary/60"
          >
            Actual vs Budget data is not available.
          </Typography>
        </div>
      </section>
    )
  }

  const cumulativeTrendAvailable = Boolean(
    data.cumulativeTrends?.some((series) => series.points.length > 0)
  )
  const selectedTrends =
    trendMode === 'CUMULATIVE_FYTD'
      ? (data.cumulativeTrends ?? [])
      : data.trends

  return (
    <section
      id="actual-vs-budget"
      aria-label="Actual vs Budget"
      className="scroll-mt-4 space-y-3"
    >
      <SectionHeader
        title="Actual vs Budget"
        afterTitle={
          <Typography as="span" variant="body-sm" className="text-primary/50">
            · FYTD
          </Typography>
        }
        right={
          data.funds ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Typography
                as="span"
                variant="body-sm"
                className="text-primary/60"
              >
                Funds
              </Typography>
              {data.funds.length > 0 ? (
                data.funds.map((fund) => (
                  <Typography
                    key={fund.id}
                    as="span"
                    variant="body-sm"
                    className="font-medium text-primary"
                    title={fund.displayName}
                  >
                    {fund.abbreviation}
                  </Typography>
                ))
              ) : (
                <Typography
                  as="span"
                  variant="body-sm"
                  className="text-primary/50"
                >
                  No funds
                </Typography>
              )}
            </div>
          ) : undefined
        }
      />

      <div className="@container px-1 pb-1 sm:px-3">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-1">
          <Typography variant="body-sm" className="text-primary/65">
            {data.fiscalContext?.fiscalYearLabel ?? 'Fiscal year unavailable'} ·
            Actuals and budget through{' '}
            {formatDate(data.latestClosedActualPeriod)}
          </Typography>
          <Typography variant="body-sm" className="text-primary/50">
            Budget coverage:{' '}
            {data.budgetCoverage.available
              ? `${formatDate(data.budgetCoverage.firstPeriod)}–${formatDate(
                  data.budgetCoverage.latestPeriod
                )}`
              : 'Not available'}
          </Typography>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-2"
            aria-label="Chart legend"
          >
            <div className="flex items-center gap-2">
              <span className="h-0.5 w-5 bg-primary" aria-hidden="true" />
              <Typography
                as="span"
                variant="body-sm"
                className="text-primary/75"
              >
                Actual
              </Typography>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="w-5 border-t-2 border-dashed border-secondary"
                aria-hidden="true"
              />
              <Typography
                as="span"
                variant="body-sm"
                className="text-primary/75"
              >
                Budget
              </Typography>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Typography as="span" variant="body-sm" className="text-primary/50">
              Chart view
            </Typography>
            <div
              role="group"
              aria-label="Chart view"
              className="inline-flex rounded-md border border-primary/10 bg-background p-0.5"
            >
              {trendModeOptions.map((option) => {
                const isActive = trendMode === option.value
                const isDisabled =
                  option.value === 'CUMULATIVE_FYTD' &&
                  !cumulativeTrendAvailable
                return (
                  <Button
                    key={option.value}
                    type="button"
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    className="h-7 rounded px-2.5 shadow-none"
                    aria-pressed={isActive}
                    disabled={isDisabled}
                    title={
                      isDisabled
                        ? 'Cumulative FYTD trend is not available.'
                        : undefined
                    }
                    onClick={() => setTrendMode(option.value)}
                  >
                    {option.label}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 divide-y divide-border @[48rem]:grid-cols-2 @[48rem]:divide-x @[48rem]:divide-y-0">
          {data.comparisons.map((comparison) => {
            const metric = comparison.requestedMetric
            const trend = selectedTrends.find(
              (item) => item.requestedMetric === metric
            )
            return (
              <MetricComparisonGroup
                key={metric}
                comparison={comparison}
                trend={trend?.points ?? []}
                trendMode={trendMode}
              />
            )
          })}
        </div>
      </div>
    </section>
  )
}

export function CompanyActualVsBudgetViewContainer({
  companyId,
}: {
  companyId: string
}) {
  const query = useCompanyActualVsBudget(
    companyId,
    true,
    FINANCIALS_ACTUAL_VS_BUDGET_METRICS
  )

  return (
    <CompanyActualVsBudgetView
      data={query.data}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => {
        void query.refetch()
      }}
    />
  )
}
